import { ageSeconds } from "../tokens";

/* Hero 3.0 palette — Figma 214:4 APPROVED REBUILD / SOURCE AUTHORITY */
export const H3 = {
  bg: "#020204", card: "#05070b", border: "#1f2636", borderTouch: "#1f293b",
  red: "#f22e33", redTouch: "#ff2e3d", redBg: "#8a0b11", redBgTouch: "#1f0305",
  violet: "#6e33ff", violetTouch: "#6e26ff", violetDeep: "#380f8c", violetBg: "#17082e",
  violetText: "#b866ff", purple: "#b885ff",
  cyan: "#1ad1f0", cyanTouch: "#21d4ed", green: "#14d185", greenTouch: "#14e59e",
  sparkGreen: "#35d14f", blue: "#086bf2", teal: "#0a8aa3", slate: "#404757",
  text: "#f5f7fc", body: "#e0e3ed", meta: "#b8bdcf", gray: "#7a8299", grayTouch: "#7d8aa3",
  amber: "#f2a900",
};

export const h3Age = (iso?: string | null): string => {
  const s = ageSeconds(iso);
  if (s == null) return "";
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/* Short age used by the 768/390 masters ("25m", "3h") */
export const h3AgeShort = (iso?: string | null): string => {
  const s = ageSeconds(iso);
  if (s == null) return "";
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/* Earned LIVE chip — payload freshness only, never decorative */
export const H3Live = ({ at, testid }: { at?: string | null; testid: string }) => {
  const s = ageSeconds(at);
  if (s != null && s <= 150)
    return <span data-testid={testid} className="nl-mono text-[10px] leading-[14px]" style={{ color: H3.green }}>● LIVE</span>;
  if (s == null) return null;
  return (
    <span data-testid={testid} className="nl-mono text-[9px] leading-[14px]" style={{ color: H3.gray }}>
      {s < 3600 ? `${Math.max(1, Math.floor(s / 60))}m AGO` : `${Math.floor(s / 3600)}h AGO`}
    </span>
  );
};

/* Canonical official asset marks (216:65-75 — identity rule, never generic) */
export const BtcMark3 = ({ size }: { size: number }) => (
  <span aria-hidden className="rounded-full inline-flex items-center justify-center shrink-0"
    style={{ width: size, height: size, background: "#f7921a", border: `1px solid ${H3.border}` }}>
    <span style={{ fontSize: size * 0.58, color: "#fff", fontWeight: 700, lineHeight: 1, transform: "rotate(12deg)" }}>₿</span>
  </span>
);
export const EthMark3 = ({ size }: { size: number }) => (
  <span aria-hidden className="rounded-full inline-flex items-center justify-center shrink-0"
    style={{ width: size, height: size, background: "#0b1020", border: `1px solid ${H3.border}` }}>
    <svg width={size * 0.44} height={size * 0.68} viewBox="0 0 12 19" fill="none">
      <path d="M6 0L11.6 9.4L6 12.9L0.4 9.4L6 0Z" fill="#b9c1d9" />
      <path d="M6 14L11.6 10.5L6 19L0.4 10.5L6 14Z" fill="#7c849c" />
    </svg>
  </span>
);
export const SolMark3 = ({ size }: { size: number }) => (
  <span aria-hidden className="rounded-full inline-flex flex-col items-center justify-center shrink-0"
    style={{ width: size, height: size, background: "#0b1020", border: "1px solid #5c6370", gap: size * 0.055 }}>
    {[0, 1, 2].map((i) => (
      <span key={i} style={{
        width: size * 0.52, height: size * 0.12, borderRadius: 1,
        background: "linear-gradient(90deg,#19e2c1 0%,#bf21ef 100%)",
        transform: `skewX(${i === 1 ? 24 : -24}deg)`,
      }} />
    ))}
  </span>
);
export const OfficialMark3 = ({ sym, size }: { sym: string; size: number }) =>
  sym === "BTC" ? <BtcMark3 size={size} /> : sym === "ETH" ? <EthMark3 size={size} /> : sym === "SOL" ? <SolMark3 size={size} /> : null;

/* 14px canonical brand-identity dots for dense impact rows (217:6-12) */
const DOT_COLORS: Record<string, string> = {
  BTC: "#f7931a", ETH: "#627eea", SOL: "#9945ff", XRP: "#23292f", ADA: "#0033ad", DOGE: "#c2a633",
};
export const AssetDot = ({ sym, size = 14 }: { sym: string; size?: number }) => (
  <span aria-hidden className="rounded-full shrink-0 inline-block"
    style={{ width: size, height: size, background: DOT_COLORS[sym] || H3.slate, border: `1px solid ${H3.border}` }} />
);

/* Real sparkline — provider data only (Kraken closes / recorded observations) */
export const H3Spark = ({ vals, color, w = 132, h = 24, sw = 1.5 }: { vals: number[]; color: string; w?: number; h?: number; sw?: number }) => {
  if (!vals || vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const pts = vals.map((v, i) =>
    `${((i / (vals.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden className="shrink-0">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};
